import { initializeApp, getApps, getApp } from 'firebase/app'
import { getFirestore, collection, getDocs, addDoc, doc, updateDoc, deleteDoc, serverTimestamp, initializeFirestore } from 'firebase/firestore'
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage'
import { getAuth, onAuthStateChanged, signInWithEmailAndPassword, signOut, User } from 'firebase/auth'

export interface Product {
  id: string
  name: string
  description?: string | null
  category?: string | null
  image_url?: string | null
  gallery?: string[] | null
  created_at?: string | null
  updated_at?: string | null
  active?: boolean
}

const win: any = typeof window !== 'undefined' ? window : {}
const cfg = (win.__APP_FIREBASE_CONFIG__ as any) || {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY as string,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN as string,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID as string,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET as string,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID as string,
  appId: process.env.REACT_APP_FIREBASE_APP_ID as string,
}

const app = !getApps().length ? initializeApp(cfg) : getApp()

// Initialize Firestore with long-polling to prevent ERR_ABORTED errors
const db = initializeFirestore(app, {
  experimentalForceLongPolling: true,
})
const storage = getStorage(app)
const auth = getAuth(app)

const mapDoc = (d: any): Product => {
  const data = d.data() || {}
  const ca = data.created_at && typeof data.created_at.toDate === 'function' ? data.created_at.toDate().toISOString() : data.created_at || null
  const ua = data.updated_at && typeof data.updated_at.toDate === 'function' ? data.updated_at.toDate().toISOString() : data.updated_at || null
  return {
    id: d.id,
    name: data.name,
    description: data.description ?? null,
    category: data.category ?? null,
    image_url: data.image_url ?? null,
    gallery: Array.isArray(data.gallery) ? data.gallery : null,
    created_at: ca,
    updated_at: ua,
    active: typeof data.active === 'undefined' ? true : !!data.active,
  }
}

export const listProducts = async (): Promise<any[]> => {
  const snap = await getDocs(collection(db, 'products'))
  const items = snap.docs.map(mapDoc)
  return items.sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''))
}

export const signIn = (email: string, password: string) =>
  signInWithEmailAndPassword(auth, email, password)

export const signOutUser = () => signOut(auth)

export const onAuth = (cb: (user: User | null) => void) => onAuthStateChanged(auth, cb)

export { auth, db, storage }

export const uploadImage = async (file: File): Promise<string> => {
  const ts = Date.now()
  const safe = (file.name || 'image').replace(/[^a-zA-Z0-9_.-]/g, '_')
  const r = ref(storage, `uploads/${ts}_${safe}`)
  await uploadBytes(r, file)
  return await getDownloadURL(r)
}

export const uploadImages = async (files: File[]): Promise<string[]> => {
  const out: string[] = []
  for (const f of files) {
    const url = await uploadImage(f)
    out.push(url)
  }
  return out
}

export const createProduct = async (payload: Omit<Product, 'id' | 'created_at' | 'updated_at'>): Promise<Product> => {
  const docRef = await addDoc(collection(db, 'products'), {
    ...payload,
    created_at: serverTimestamp(),
    updated_at: null,
    active: typeof payload.active === 'undefined' ? true : !!payload.active,
  })
  const d = { id: docRef.id, ...payload, created_at: new Date().toISOString(), updated_at: null, active: typeof payload.active === 'undefined' ? true : !!payload.active }
  return d
}

export const updateProduct = async (id: string, payload: Partial<Product>): Promise<void> => {
  await updateDoc(doc(db, 'products', id), { ...payload, updated_at: serverTimestamp() })
}

export const deleteProductById = async (id: string): Promise<void> => {
  await deleteDoc(doc(db, 'products', id))
}